import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Building2, TrendingUp, ArrowUpRight, Clock, CreditCard, Wallet, Loader2 } from "lucide-react";
import { useBusinessContext } from "@/providers/business-provider";
import { useLocation } from "wouter";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { format } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { safe } from "@/lib/utils";

declare global {
  interface Window { Plaid: any; }
}

interface BankRegisterAccount {
  id: number;
  name: string;
  type: string;
  balance: string;
  accountNumber?: string | null;
  description?: string;
  lastSyncedAt?: string | null;
}

export function BankAccountsSection() {
  const { activeBusiness } = useBusinessContext();
  const [, navigate] = useLocation();
  const { toast } = useToast();
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isConnecting, setIsConnecting] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const { data: bankAccounts = [], isLoading } = useQuery<BankRegisterAccount[]>({
    queryKey: [`/api/bank-register-accounts/${activeBusiness?.id}`],
    enabled: !!activeBusiness?.id,
  });

  // Reactive upgrade flow: button always opens Plaid Link directly. If the
  // server (enforceBankLimit) blocks with PLAN_LIMIT_BANK_ACCOUNTS_EXCEEDED,
  // we surface this dialog instead of a toast — user can confirm the $15/mo
  // add-on right then and we automatically retry the connect.
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [upgradeContext, setUpgradeContext] = useState<{ current: number; max: number } | null>(null);
  const [isUpgrading, setIsUpgrading] = useState(false);

  const upgradeAndRetry = async () => {
    if (!activeBusiness?.id) return;
    setIsUpgrading(true);
    try {
      const res = await apiRequest("POST", "/api/billing/add-bank-addon", {
        businessId: activeBusiness.id,
        quantity: 1,
      });
      const data = await res.json();
      if (!res.ok) {
        if (data?.code === "BANK_ADDON_NOT_CONFIGURED") {
          toast({
            title: "Upgrade unavailable",
            description: "Bank account add-on isn't enabled yet — please contact support.",
            variant: "destructive",
          });
        } else {
          throw new Error(data?.message || "Upgrade failed");
        }
        return;
      }
      if (data?.checkoutUrl) {
        // Trial users → Stripe Checkout. Resume connect after they return.
        window.location.href = data.checkoutUrl;
        return;
      }
      toast({ title: "Bank slot added", description: `Opening Plaid to connect your account...` });
      setShowUpgradeDialog(false);
      // Auto-retry the bank connect now that the slot exists.
      setTimeout(() => connectBank(), 250);
    } catch (err: any) {
      toast({ title: "Upgrade failed", description: err.message, variant: "destructive" });
    } finally {
      setIsUpgrading(false);
    }
  };

  const connectBank = async () => {
    if (!activeBusiness?.id) {
      toast({ title: "No business selected", variant: "destructive" });
      return;
    }

    setIsConnecting(true);
    try {
      // 1. Get link token — server enforces the bank limit here. If we hit
      //    it, open the upgrade dialog instead of failing with a toast.
      const res = await apiRequest("POST", `/api/businesses/${activeBusiness.id}/plaid/link-token`);
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        if (err?.code === "PLAN_LIMIT_BANK_ACCOUNTS_EXCEEDED") {
          setUpgradeContext({ current: err.current ?? 1, max: err.max ?? 1 });
          setShowUpgradeDialog(true);
          setIsConnecting(false);
          return;
        }
        throw new Error(err.message || "Failed to create link token");
      }
      const { link_token } = await res.json();
      if (!link_token) throw new Error("No link token received from server");

      // 2. Check Plaid SDK
      if (!window.Plaid) throw new Error("Plaid Link not loaded. Please refresh the page.");

      // 3. Open Plaid Link
      const handler = window.Plaid.create({
        token: link_token,
        onSuccess: async (public_token: string, metadata: any) => {
          try {
            const exchangeRes = await apiRequest("POST", `/api/businesses/${activeBusiness.id}/plaid/exchange-token`, {
              publicToken: public_token,
              institutionName: metadata?.institution?.name || null,
            });
            if (!exchangeRes.ok) {
              const err = await exchangeRes.json();
              throw new Error(err.message || "Failed to link account");
            }
            toast({ title: "Bank Connected", description: `${metadata?.institution?.name || "Your bank"} has been linked successfully.` });
            queryClient.invalidateQueries({ queryKey: [`/api/bank-register-accounts/${activeBusiness.id}`] });
            queryClient.invalidateQueries({ queryKey: [`/api/businesses/${activeBusiness.id}/plaid/connection-status`] });
          } catch (err: any) {
            toast({ title: "Connection failed", description: err.message, variant: "destructive" });
          } finally {
            setIsConnecting(false);
          }
        },
        onExit: () => setIsConnecting(false),
        onEvent: (event: string) => console.log("[Plaid]", event),
      });

      handler.open();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
      setIsConnecting(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="border-blue-200 shadow-sm bg-gradient-to-br from-blue-50 to-indigo-50">
        <CardContent className="p-6">
          <div className="animate-pulse">
            <div className="h-4 bg-gray-200 rounded w-1/3 mb-4"></div>
            <div className="h-8 bg-gray-200 rounded w-1/2 mb-4"></div>
            <div className="h-4 bg-gray-200 rounded w-full"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  const primaryAccount = bankAccounts.find((account) =>
    account.name.toLowerCase().includes('cash') ||
    account.name.toLowerCase().includes('bank') ||
    account.name.toLowerCase().includes('checking')
  ) || bankAccounts[0];

  const bankAccount = primaryAccount ? {
    name: primaryAccount.name,
    accountNumber: primaryAccount.accountNumber || `Account ${primaryAccount.id}`,
    bankBalance: parseFloat(primaryAccount.balance) || 0.00,
    quickBooksBalance: parseFloat(primaryAccount.balance) || 0.00,
    lastUpdated: format(new Date(), "MMM dd, yyyy 'at' h:mm a"),
    needsReview: 0,
    bankName: `${safe(activeBusiness?.name) || "Business"} Banking`,
    accountType: primaryAccount.type || "Business Account",
    isConnected: true
  } : null;

  // Shared dialog rendered after both return paths via React.Fragment.
  const upgradeDialog = (
    <Dialog open={showUpgradeDialog} onOpenChange={setShowUpgradeDialog}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Bank account limit reached</DialogTitle>
          <DialogDescription className="space-y-2">
            <span className="block">
              <strong>{(activeBusiness as any)?.name?.trim() || "This business"}</strong> is connected to{" "}
              <strong>{upgradeContext?.current ?? 1} of {upgradeContext?.max ?? 1}</strong> banks.
            </span>
            <span className="block">
              Add a bank slot for <strong>$15/month</strong> (prorated today) to connect another bank
              or credit card.
            </span>
            <span className="block text-xs">
              Each business comes with 1 bank connection. Each extra slot is billed as a separate
              line item on your existing subscription.
            </span>
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => setShowUpgradeDialog(false)} disabled={isUpgrading}>
            Cancel
          </Button>
          <Button onClick={upgradeAndRetry} disabled={isUpgrading}>
            {isUpgrading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
            {isUpgrading ? "Adding slot..." : "Add slot ($15/mo) & connect"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );

  if (!bankAccount) {
    return (
      <>
      <Card className="border border-blue-100 shadow-sm bg-gradient-to-br from-blue-50/60 via-white to-indigo-50/60 rounded-2xl">
        <CardContent className="flex flex-col items-center justify-center text-center p-8 sm:p-10 max-w-md mx-auto">
          <div className="h-14 w-14 rounded-2xl bg-blue-100 flex items-center justify-center mb-5">
            <Building2 className="h-7 w-7 text-blue-600" />
          </div>
          <Badge variant="secondary" className="mb-4 bg-blue-50 text-blue-700 border-blue-200 text-[11px] uppercase tracking-wider font-semibold">
            Recommended
          </Badge>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No Bank Connected</h3>
          <p className="text-sm text-gray-500 leading-relaxed mb-6">
            Connect your bank to automatically import transactions and unlock AI-powered bookkeeping.
          </p>
          <Button className="bg-blue-600 hover:bg-blue-700 shadow-sm h-10 px-6" onClick={connectBank} disabled={isConnecting}>
            {isConnecting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Building2 className="h-4 w-4 mr-2" />}
            {isConnecting ? "Connecting..." : "Connect Bank"}
          </Button>
          <p className="text-[11px] text-gray-400 mt-4">Secured by Plaid. 256-bit encryption.</p>
        </CardContent>
      </Card>
      {upgradeDialog}
      </>
    );
  }

  return (
    <>
    <Card className="relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-indigo-50 border-blue-200/60 shadow-lg">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="absolute top-4 right-4">
          <Building2 className="h-32 w-32 text-blue-600" />
        </div>
      </div>

      <CardHeader className="relative pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-3 bg-blue-600 rounded-xl shadow-md">
              <Wallet className="h-6 w-6 text-white" />
            </div>
            <div>
              <CardTitle className="text-lg font-semibold text-gray-900">
                Bank Accounts
              </CardTitle>
              <p className="text-sm text-gray-600 mt-1">
                Real-time account overview for {activeBusiness?.name}
              </p>
            </div>
          </div>
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
            Live
          </Badge>
        </div>
      </CardHeader>

      <CardContent className="relative pt-2">
        <div className="bg-white/80 backdrop-blur-sm rounded-xl p-6 border border-blue-100 shadow-sm">
          {/* Account Header */}
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <CreditCard className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{bankAccount.name}</h3>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Account #{bankAccount.accountNumber}</span>
                  <span>•</span>
                  <span>{bankAccount.bankName}</span>
                </div>
              </div>
            </div>
            <Badge
              variant="secondary"
              className="bg-green-100 text-green-800 border-green-200"
            >
              {bankAccount.accountType}
            </Badge>
          </div>

          {/* Balance Display */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">Current Balance</span>
                <TrendingUp className="h-4 w-4 text-green-500" />
              </div>
              <div className="text-3xl font-bold text-gray-900">
                ${bankAccount.bankBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
              <p className="text-sm text-gray-500">Available funds</p>
            </div>

            <div className="space-y-2">
              <span className="text-sm font-medium text-gray-600">ZentroBooks Balance</span>
              <div className="text-2xl font-semibold text-gray-800">
                ${bankAccount.quickBooksBalance.toLocaleString('en-US', { minimumFractionDigits: 2 })}
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span className="text-sm text-green-600">Synced</span>
              </div>
            </div>
          </div>

          {/* Status Row */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <Clock className="h-4 w-4" />
              <span>Last updated: {bankAccount.lastUpdated}</span>
            </div>

            <div className="flex items-center gap-3">
              {bankAccount.needsReview === 0 ? (
                <span className="text-sm text-green-600 font-medium">
                  All transactions reviewed
                </span>
              ) : (
                <Badge variant="outline" className="text-orange-600 border-orange-200">
                  {bankAccount.needsReview} need review
                </Badge>
              )}

              <Button
                size="sm"
                variant="outline"
                className="text-blue-600 border-blue-200 hover:bg-blue-50"
                onClick={() => navigate("/transaction-register")}
              >
                <ArrowUpRight className="h-4 w-4 mr-1" />
                View Register
              </Button>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="flex items-center justify-between mt-4 pt-4 border-t border-blue-100">
          <div className="text-sm text-gray-600">
            <span className="font-medium">1 account connected</span> • Last sync: {format(currentTime, "h:mm a")}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-blue-600 hover:bg-blue-50 hover:text-black focus-visible:text-black"
            onClick={connectBank}
            disabled={isConnecting}
          >
            {isConnecting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Add Another Account
          </Button>
        </div>
      </CardContent>
    </Card>
    {upgradeDialog}
    </>
  );
}
